import React from "react";
import { Card } from "@/components/ui/card";
import InfoTooltip from "@/components/ui/InfoTooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { WidgetTitle } from "@/components/widgets/WidgetTitle";
import CollapseToggleButton from "@/components/widgets/common/CollapseToggleButton";
import { cn } from "@/lib/utils";
import { fetchQueueCallVolumeWidget, QueueCallVolumeStats } from "@/lib/axios/api/widgets.api";
import { getPresenceColor } from "@/components/widgets/call-overview/callStatusColors";

// TCX-6287 Queue tab: per-queue Call Volume widget, ported from teamscx-realtime-report's
// QueueCallVolumeWidget (hero total + 4 stat tiles) onto POST /queue/widgets (widgetId=1).
// TCX-4302: each stat tile drills into Call Journey filtered to that status's call ids.
// UI 1:1 port of QueueHeroStatLayout/QueueMetricFlashValue from teamscx-realtime-report.

export function clampNonNegativeInt(v: number): number {
  const n = Math.floor(Number(v));
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, n);
}

export function stepsBetween(from: number, to: number): number[] {
  const a = clampNonNegativeInt(from);
  const b = clampNonNegativeInt(to);
  if (a === b) return [];
  const out: number[] = [];
  if (b > a) {
    for (let i = a + 1; i <= b; i++) out.push(i);
  } else {
    for (let i = a - 1; i >= b; i--) out.push(i);
  }
  return out;
}

/** Match CSS duration `animate-queue-metric-roll-up` (~620ms). */
const ROLL_DURATION_MS = 620;
const STEP_GAP_MS = 150;
const STEP_INTERVAL_MS = ROLL_DURATION_MS + STEP_GAP_MS;
const MAX_COUNT_STEPS = 32;

export const QueueMetricFlashValue: React.FC<{ value: number; className?: string }> = ({ value, className }) => {
  const initial = clampNonNegativeInt(value);
  const [displayed, setDisplayed] = React.useState(initial);
  const [rollKey, setRollKey] = React.useState(0);
  const displayedRef = React.useRef(initial);
  const timeoutsRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimeouts = () => {
    for (const t of timeoutsRef.current) clearTimeout(t);
    timeoutsRef.current = [];
  };

  React.useEffect(() => {
    const to = clampNonNegativeInt(value);
    const from = displayedRef.current;
    if (from === to) return;

    clearTimeouts();
    const path = stepsBetween(from, to);
    if (path.length === 0) return;

    const run = (next: number) => {
      displayedRef.current = next;
      setDisplayed(next);
      setRollKey((k) => k + 1);
    };

    if (path.length > MAX_COUNT_STEPS) {
      const t = setTimeout(() => run(to), 0);
      timeoutsRef.current.push(t);
      return clearTimeouts;
    }

    path.forEach((step, index) => {
      const t = setTimeout(() => run(step), index * STEP_INTERVAL_MS);
      timeoutsRef.current.push(t);
    });

    return clearTimeouts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <span
      className={cn(
        "inline-flex h-[1.2em] min-w-[0.6em] items-end justify-center overflow-hidden tabular-nums align-bottom",
        className
      )}
    >
      <span key={rollKey} className="inline-block leading-none animate-queue-metric-roll-up">
        {displayed}
      </span>
    </span>
  );
};

export type QueueCallVolumeStatus = "answered" | "missed" | "transferred" | "abandoned";

export const QUEUE_CALL_VOLUME_CALL_IDS_KEY: Record<QueueCallVolumeStatus, keyof QueueCallVolumeStats> = {
  answered: "totalCallIdOfferedAnswered",
  missed: "totalCallIdOfferedMissed",
  transferred: "totalCallIdOfferedTransferred",
  abandoned: "totalCallIdOfferedAbandoned",
};

interface QueueVolumeWidgetProps {
  queueMicrosoftId: string;
  queueName: string;
  startDate: string;
  endDate: string;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onStatClick?: (status: QueueCallVolumeStatus, callIds: string[]) => void;
}

const METRICS: Array<{ key: QueueCallVolumeStatus; label: string; colorClass: string; valueClassName: string }> = [
  {
    key: "answered",
    label: "Answered",
    colorClass: getPresenceColor("Answered").replace(" font-semibold", ""),
    valueClassName: "items-center align-middle font-normal",
  },
  {
    key: "missed",
    label: "Missed",
    colorClass: getPresenceColor("Missed").replace(" font-semibold", ""),
    valueClassName: "items-center align-middle font-normal",
  },
  {
    key: "transferred",
    label: "Transferred",
    colorClass: getPresenceColor("Transferred").replace(" font-semibold", ""),
    valueClassName: "items-center align-middle",
  },
  {
    key: "abandoned",
    label: "Abandoned",
    colorClass: getPresenceColor("Abandoned").replace(" font-semibold", ""),
    valueClassName: "items-center align-middle",
  },
];

const QueueVolumeWidget: React.FC<QueueVolumeWidgetProps> = ({
  queueMicrosoftId,
  queueName,
  startDate,
  endDate,
  collapsed = false,
  onToggleCollapse,
  onStatClick,
}) => {
  const [data, setData] = React.useState<QueueCallVolumeStats | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!queueMicrosoftId || !startDate || !endDate) return;
    let cancelled = false;
    const controller = new AbortController();

    setLoading(true);
    fetchQueueCallVolumeWidget(
      { startTime: startDate, endTime: endDate, queueMicrosoftId },
      { signal: controller.signal }
    )
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch((err) => {
        if (cancelled || err?.name === "CanceledError" || err?.name === "AbortError") return;
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [queueMicrosoftId, startDate, endDate]);

  const renderBody = () => {
    if (loading) return <Skeleton className="h-full min-h-[150px] w-full flex-1" />;
    return (
      <div className="grid min-h-0 min-w-0 flex-1 grid-rows-[minmax(0,3fr)_minmax(0,2fr)] overflow-hidden">
        <div className="flex min-h-0 flex-col items-center justify-center px-1 sm:px-2">
          <div className="mx-auto flex w-full max-w-xs flex-col items-center justify-center px-6 py-8 text-center sm:max-w-sm sm:py-10">
            <QueueMetricFlashValue
              value={data?.totalInboundCalls ?? 0}
              className={cn(
                "text-2xl font-bold tabular-nums text-primary dark:text-white",
                "text-2xl sm:text-3xl md:text-4xl tracking-tight"
              )}
            />
            <div className={cn("text-xs text-foreground mt-1 leading-normal", "mt-2 font-medium sm:text-[0.8125rem]")}>
              Total Offered Calls
            </div>
          </div>
        </div>
        <div className="flex min-h-0 flex-col justify-center border-t pt-3">
          <div className="grid w-full min-w-0 grid-cols-4 gap-x-1.5 gap-y-0 sm:gap-x-2 md:gap-x-3">
            {METRICS.map((m) => {
              const count = (data?.[m.key] as number | undefined) ?? 0;
              const callIds = (data?.[QUEUE_CALL_VOLUME_CALL_IDS_KEY[m.key]] as string[] | undefined) ?? [];
              const clickable = count > 0 && callIds.length > 0 && !!onStatClick;
              return (
                <div key={m.key} className="min-w-0 text-center">
                  <button
                    type="button"
                    disabled={!clickable}
                    onClick={clickable ? () => onStatClick?.(m.key, callIds) : undefined}
                    aria-label={`View ${m.label} calls`}
                    className={cn(
                      "inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      clickable && "cursor-pointer"
                    )}
                  >
                    <QueueMetricFlashValue
                      value={count}
                      className={cn("text-2xl font-medium tabular-nums leading-none", m.colorClass, m.valueClassName)}
                    />
                  </button>
                  <div className={cn("text-xs text-foreground leading-normal", "mt-0.5")}>{m.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className={cn("box-border flex min-h-0 flex-col overflow-hidden dark:border-[#0C3451]", collapsed ? "p-0" : "h-full p-2")}>
      <div className={cn("flex min-w-0 shrink-0 items-center justify-between gap-2", collapsed && "px-2 py-1.5")}>
        <div className="flex items-center gap-2 min-w-0">
          <WidgetTitle className="truncate">{queueName ? `Call volume - ${queueName}` : "Queue Volume"}</WidgetTitle>
          <InfoTooltip content="Total offered calls for the selected Call Queue, broken down into Answered, Missed, Transferred, and Abandoned within the given time." />
        </div>
        {onToggleCollapse && <CollapseToggleButton collapsed={collapsed} onToggle={onToggleCollapse} />}
      </div>
      {!collapsed && renderBody()}
    </Card>
  );
};

export default QueueVolumeWidget;
